import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  Container,
  Typography,
  Box,
  Button,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  CardActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Ronda from './components/Ronda';
import Baciga from './components/Baciga';
import GameSetup from './components/GameSetup';
import Instructions from './components/Instructions';
import Header from './components/Header';
import GameModeModal from './components/GameModeModal';
import { AuthProvider } from './context/AuthContext';
import RequireOnlineAuth from './components/online/RequireOnlineAuth';
import OnlineAuthScreen from './components/online/OnlineAuthScreen';
import OnlineLobbyScreen from './components/online/OnlineLobbyScreen';
import OnlineCreateRoomScreen from './components/online/OnlineCreateRoomScreen';
import WaitingRoomScreen from './components/online/WaitingRoomScreen';
import OnlinePlayScreen from './components/online/OnlinePlayScreen';
import JoinByLinkScreen from './components/online/JoinByLinkScreen';
import OnlineHistoryScreen from './components/online/OnlineHistoryScreen';
import OnlineRankingsScreen from './components/online/OnlineRankingsScreen';
import OnlineProfileScreen from './components/online/OnlineProfileScreen';
import DeepLinkListener from './components/online/DeepLinkListener';
import OAuthCallbackScreen from './components/online/OAuthCallbackScreen';
import RequireAdmin from './components/admin/RequireAdmin';
import AdminHomeScreen from './components/admin/AdminHomeScreen';
import AdminUsersScreen from './components/admin/AdminUsersScreen';
import AdminRoomsScreen from './components/admin/AdminRoomsScreen';
import AdminSuggestionsScreen from './components/admin/AdminSuggestionsScreen';
import AdminErrorsScreen from './components/admin/AdminErrorsScreen';
import AdminRankingsScreen from './components/admin/AdminRankingsScreen';
import AdminUsageStatsScreen from './components/admin/AdminUsageStatsScreen';
import AdminAppVersionScreen from './components/admin/AdminAppVersionScreen';
import AdminNotificationsScreen from './components/admin/AdminNotificationsScreen';
import ImageCarousel from './components/ImageCarousel';
import ConcordarePromo from './components/ConcordarePromo';
import VersionCheckManager from './components/VersionCheckManager';
import DeviceTokenRegistrar from './components/DeviceTokenRegistrar';
import SavedGameModal from './components/SavedGameModal';
import ChangelogModal from './components/ChangelogModal';
import TutorialModal from './components/TutorialModal';
import gameStateManager from './services/GameStateManager';
import adMobService from './services/AdMobService';
import changelogService from './services/ChangelogService';
import tutorialService from './services/TutorialService';
import soundVibrationService from './services/SoundVibrationService';
import { shareImageService } from './services/ShareImageService';
import ratingService from './services/RatingService';
import { usePageTracking } from './hooks/usePageTracking';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd } from './utils/debugUtils';

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [savedGameModalOpen, setSavedGameModalOpen] = useState(false);
  const [savedGameInfo, setSavedGameInfo] = useState(null);
  const [changelogModalOpen, setChangelogModalOpen] = useState(false);
  const [shouldShowChangelog, setShouldShowChangelog] = useState(false);
  const [tutorialModalOpen, setTutorialModalOpen] = useState(false);
  const [isTestingShare, setIsTestingShare] = useState(false);
  const [isTestingReview, setIsTestingReview] = useState(false);
  const [modeModalOpen, setModeModalOpen] = useState(false);
  const [isAdLoading, setIsAdLoading] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);
  const [rewardedReady, setRewardedReady] = useState(false);
  const [adCheckPending, setAdCheckPending] = useState(true);
  const currentVersion = process.env.REACT_APP_VERSION;
  const isDebugMode = process.env.REACT_APP_DEBUG_MODE === 'true';
  const isWebPlatform = Capacitor.getPlatform() === 'web';

  // Safety timeout for the loading spinner (10 seconds)
  const AD_LOADING_TIMEOUT_MS = 10000;
  // Safety timeout for the initial ad-readiness check (10 seconds), so the
  // mode buttons don't stay disabled forever if the ad SDK never fires a
  // load/fail event (e.g. a hung network call)
  const AD_PREPARE_TIMEOUT_MS = 10000;

  // Test in-app review dialog
  const handleTestReview = async () => {
    setIsTestingReview(true);
    try {
      const { InAppReview } = await import('@capacitor-community/in-app-review');
      await InAppReview.requestReview();
    } catch (error) {
      debugError('Error testing in-app review:', error);
    } finally {
      setIsTestingReview(false);
    }
  };

  // Test share functionality with mock data
  const handleTestShare = async () => {
    setIsTestingShare(true);
    try {
      const mockPlayers = [
        { name: 'Carlos', avatar: '😎' },
        { name: 'María', avatar: '👩' },
        { name: 'Pedro', avatar: '🧔' },
        { name: 'Ana', avatar: '👧' }
      ];
      await shareImageService.shareWinnerImage({
        winnerTeamId: 1,
        players: mockPlayers,
        gamePoints: 2,
        t
      });
    } catch (error) {
      debugError('Error testing share:', error);
    } finally {
      setIsTestingShare(false);
    }
  };

  // Check for saved game on mount
  useEffect(() => {
    const checkSavedGame = async () => {
      try {
        const info = await gameStateManager.getSavedGameInfo();
        if (info) {
          setSavedGameInfo(info);
          setSavedGameModalOpen(true);
        }
      } catch (error) {
        debugError('Error checking for saved game:', error);
      }
    };
    checkSavedGame();
  }, []);

  // Check if there are unread changelog entries on mount, to show a badge on
  // the header icon instead of popping the modal open automatically.
  useEffect(() => {
    try {
      setShouldShowChangelog(changelogService.shouldShowChangelog(currentVersion));
    } catch (error) {
      debugError('Error checking changelog:', error);
    }
  }, [currentVersion]);

  // Prepare the rewarded ad, tracking whether the load attempt is still in
  // flight so the mode selection buttons can wait for it to settle (ready
  // or not) instead of letting the user pick a mode mid-check.
  const prepareRewardedAd = useCallback(async () => {
    setAdCheckPending(true);
    try {
      const ready = await Promise.race([
        adMobService.prepareRewarded(),
        new Promise((resolve) => setTimeout(() => resolve(false), AD_PREPARE_TIMEOUT_MS))
      ]);
      debugLog('Rewarded ad prepared:', ready);
      setRewardedReady(ready);
    } catch (error) {
      debugLog('AdMob rewarded preparation failed:', error);
      setRewardedReady(false);
    } finally {
      setAdCheckPending(false);
    }
  }, []);

  // Prepare the rewarded ad on Home mount to give it more time to load.
  // This increases the likelihood that an ad will be ready when the user
  // picks a game mode (both offline and online share this single ad type).
  useEffect(() => {
    const prepareAd = async () => {
      await adMobService.initialize();
      await prepareRewardedAd();
    };
    prepareAd();
  }, [prepareRewardedAd]);

  // Safety timeout for ad loading - if ad doesn't dismiss in time, navigate anyway
  useEffect(() => {
    if (!isAdLoading || !pendingRoute) return;

    const timeoutId = setTimeout(() => {
      debugLog('Ad loading timeout reached, navigating directly');
      setIsAdLoading(false);
      navigate(pendingRoute);
      setPendingRoute(null);
      prepareRewardedAd();
    }, AD_LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isAdLoading, pendingRoute, navigate, prepareRewardedAd]);

  // Listen for rewarded ad dismissed event and navigate
  useEffect(() => {
    if (!pendingRoute) return;

    const listeners = adMobService.addRewardedListeners({
      onDismissed: () => {
        setIsAdLoading(false);
        navigate(pendingRoute);
        setPendingRoute(null);
        prepareRewardedAd();
      },
      onFailed: () => {
        setIsAdLoading(false);
        navigate(pendingRoute);
        setPendingRoute(null);
        prepareRewardedAd();
      }
    });

    return () => listeners.remove();
  }, [pendingRoute, navigate, prepareRewardedAd]);

  // Show the rewarded ad (if ready) before navigating to the chosen game mode.
  // The mode buttons are disabled until adCheckPending is false, so by the
  // time this runs the ad has either loaded or definitively failed to load.
  const handleSelectGameMode = async (route) => {
    setModeModalOpen(false);

    if (!rewardedReady || !adMobService.isRewardedReady()) {
      debugLog('Rewarded ad not ready, navigating directly without ad');
      navigate(route);
      prepareRewardedAd();
      return;
    }

    setIsAdLoading(true);
    setPendingRoute(route);

    const adShown = await adMobService.showRewarded(5000);

    if (!adShown) {
      debugLog('Rewarded ad failed to show, navigating directly');
      setIsAdLoading(false);
      setPendingRoute(null);
      navigate(route);
      prepareRewardedAd();
    }
    // If adShown is true, the ad is showing and navigation will happen via onDismissed listener
  };

  // Handle tutorial close (X button) - will show again next time
  const handleTutorialClose = () => {
    setTutorialModalOpen(false);
  };

  // Handle tutorial "Don't show again"
  const handleTutorialDontShowAgain = () => {
    tutorialService.dismissTutorial();
    setTutorialModalOpen(false);
  };

  // Handle resume saved game
  const handleResumeSavedGame = async () => {
    debugLog('═══════════════════════════════════════════════════════');
    debugLog('📂 APP: Iniciando carga de partida guardada');
    debugLog('═══════════════════════════════════════════════════════');

    setSavedGameModalOpen(false);

    if (savedGameInfo) {
      debugLog('📋 savedGameInfo:', savedGameInfo);

      // CAMBIO: Cargar estado completo
      const fullState = await gameStateManager.loadGameState();
      debugLog('💾 fullState cargado desde IndexedDB:', fullState);
      debugLog('💾 fullState.state COMPLETO:', JSON.stringify(fullState.state, null, 2));

      if (fullState && fullState.state) {
        const navigationState = {
          resumeGame: true,
          savedGameState: fullState.state,
          players: fullState.state.players || [],
          gamePoints: fullState.state.gamePoints,
          gameMode: fullState.state.gameMode,
          globalScore: fullState.state.globalScore
        };

        debugLog('🧭 Navegando a:', `/${savedGameInfo.gameType}`);
        debugLog('📦 Con estado de navegación:', navigationState);
        debugLog('   - resumeGame:', navigationState.resumeGame);
        debugLog('   - savedGameState exists:', !!navigationState.savedGameState);
        debugLog('   - players:', navigationState.players);
        debugLog('   - team1Score:', navigationState.savedGameState?.team1Score);
        debugLog('   - team2Score:', navigationState.savedGameState?.team2Score);
        debugLog('═══════════════════════════════════════════════════════');

        // Navegar CON el estado completo, incluyendo players y configuración
        navigate(`/${savedGameInfo.gameType}`, { state: navigationState });
      } else {
        debugError('❌ Failed to load saved game state');
        debugLog('fullState:', fullState);
        // Fallback: ir a setup
        navigate('/setup');
      }
    } else {
      debugError('❌ No savedGameInfo available');
    }
  };

  // Handle decline - start new game
  const handleDeclineSavedGame = async () => {
    await gameStateManager.clearSavedGame();
    setSavedGameModalOpen(false);
    setSavedGameInfo(null);
  };

  // Handle opening the changelog from the header icon - viewing it clears the unread badge
  const handleOpenChangelog = () => {
    setChangelogModalOpen(true);
    changelogService.acknowledgeChangelog(currentVersion);
    setShouldShowChangelog(false);
  };

  // Handle changelog close (X button)
  const handleChangelogClose = () => {
    setChangelogModalOpen(false);
  };

  // Handle changelog "Close and don't show again"
  const handleChangelogCloseAndDontShow = () => {
    changelogService.acknowledgeChangelog(currentVersion);
    setChangelogModalOpen(false);
  };

  return (
    <>
      {/* Loading overlay while the rewarded ad is showing */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
        open={isAdLoading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit">
            {t('common.loading', 'Cargando...')}
          </Typography>
        </Box>
      </Backdrop>

      <Header
        title={t('app.title')}
        showSettings={true}
        onChangelog={handleOpenChangelog}
        hasUnreadChangelog={shouldShowChangelog}
      />
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1, sm: 2 },
        position: 'relative',
        py: { xs: 1, sm: 2 },
        '@media (orientation: portrait)': { alignItems: 'flex-start', pb: '110px', px: 2, py: 2 }
      }}>
        <Container maxWidth={{ xs: 'sm', md: 'lg' }} sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: { xs: 0.75, sm: 2 },
            width: '100%',
            alignItems: 'stretch',
            pr: { xs: '90px', md: '120px' },
            '@media (orientation: portrait)': { flexDirection: 'column', pr: 0, gap: 1.5 }
          }}>
            {/* Left column: Description and Instructions */}
            <Box sx={{ flex: 1, display: 'flex', '@media (orientation: portrait)': { flex: 'none' } }}>
              <Card elevation={3} sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <CardContent sx={{
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  py: { xs: 0.5, sm: 2 },
                  px: { xs: 0.75, sm: 2 },
                  '@media (orientation: portrait)': { py: 1.5, px: 1.5 }
                }}>
                  <Typography variant="body1" color="text.secondary" sx={{
                    fontSize: { xs: '0.65rem', sm: '1rem' },
                    lineHeight: { xs: 1.2, sm: 1.5 },
                    '@media (orientation: portrait)': { fontSize: '0.9rem', lineHeight: 1.4 }
                  }}>
                    {t('app.description')}
                  </Typography>
                </CardContent>
                <Box sx={{ p: { xs: 0.75, sm: 2 }, pt: 0, display: 'flex', gap: { xs: 0.5, sm: 1 }, '@media (orientation: portrait)': { p: 1.5, pt: 0, gap: 1 } }}>
                  {/* Instrucciones y Tutorial en una sola fila */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    onClick={() => navigate('/instructions')}
                    sx={{
                      py: { xs: 0.4, sm: 1 },
                      fontSize: { xs: '0.6rem', sm: '1rem' },
                      minHeight: { xs: '28px', sm: '42px' },
                      '@media (orientation: portrait)': { py: 1, fontSize: '0.85rem', minHeight: '40px' }
                    }}
                  >
                    {t('buttons.instructions')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    fullWidth
                    onClick={() => setTutorialModalOpen(true)}
                    sx={{
                      py: { xs: 0.4, sm: 1 },
                      fontSize: { xs: '0.6rem', sm: '1rem' },
                      minHeight: { xs: '28px', sm: '42px' },
                      '@media (orientation: portrait)': { py: 1, fontSize: '0.85rem', minHeight: '40px' }
                    }}
                  >
                    {t('buttons.view_tutorial')}
                  </Button>
                  {isDebugMode && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="large"
                      fullWidth
                      onClick={handleTestShare}
                      disabled={isTestingShare}
                    >
                      {isTestingShare ? 'Generando...' : '🧪 Test Share'}
                    </Button>
                  )}
                  {isDebugMode && (
                    <Button
                      variant="outlined"
                      color="warning"
                      size="large"
                      fullWidth
                      onClick={handleTestReview}
                      disabled={isTestingReview}
                    >
                      {isTestingReview ? 'Abriendo...' : '⭐ Test Review'}
                    </Button>
                  )}
                </Box>
              </Card>
            </Box>

            {/* Right column: Sponsors */}
            <Box sx={{ flex: 1, display: 'flex', '@media (orientation: portrait)': { flex: 'none' } }}>
              <Card elevation={3} sx={{ width: '100%' }}>
                <CardContent sx={{
                  p: { xs: 0.5, sm: 1.5 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#f5f5f5',
                  '&:last-child': { pb: { xs: 0.5, sm: 1.5 } },
                  '@media (orientation: portrait)': { p: 1.5, '&:last-child': { pb: 1.5 } }
                }}>
                  <Typography variant="subtitle2" color="text.primary" sx={{
                    mb: { xs: 0, sm: 0.5 },
                    textAlign: 'center',
                    flexShrink: 0,
                    fontSize: { xs: '0.65rem', sm: '0.875rem' },
                    '@media (orientation: portrait)': { fontSize: '0.85rem', mb: 0.5 }
                  }}>
                    {t('sponsors.title')}
                  </Typography>
                  <Box sx={{ flexGrow: 1, minHeight: 0, display: 'flex', alignItems: 'flex-start', mt: { xs: -0.25, sm: 0 }, '@media (orientation: portrait)': { mt: 0 } }}>
                    <ImageCarousel />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Cross-promo for Concordare (another game by the same dev) -
              portrait only on native, always visible on web (replaces the
              inline ad), see ConcordarePromo for why */}
          {isWebPlatform ? (
            <Box sx={{
              width: '100%',
              pr: { xs: '90px', md: '120px' },
              '@media (orientation: portrait)': { pr: 0 }
            }}>
              <ConcordarePromo alwaysVisible />
            </Box>
          ) : (
            <ConcordarePromo />
          )}
        </Container>

        {/* Floating play button - bottom right - opens the game mode selection */}
        <IconButton
          onClick={() => setModeModalOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            width: { xs: 70, sm: 90 },
            height: { xs: 70, sm: 90 },
            '@media (orientation: portrait)': { width: 80, height: 80 },
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.5)',
            transition: 'all 0.3s ease',
            zIndex: 1000,
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              boxShadow: '0 12px 35px rgba(102, 126, 234, 0.7)',
              transform: 'scale(1.1)',
            }
          }}
        >
          <PlayArrowIcon sx={{ fontSize: { xs: 40, sm: 48 }, '@media (orientation: portrait)': { fontSize: 44 } }} />
        </IconButton>
      </Box>

      {/* Game mode selection modal - shows the rewarded ad on selection.
          Buttons stay disabled while the ad check is still in flight, so
          native players don't pick a mode before we know whether an ad
          will play. On web there's no rewarded ad, so it's never pending. */}
      <GameModeModal
        open={modeModalOpen}
        onClose={() => setModeModalOpen(false)}
        onSelectOffline={() => handleSelectGameMode('/setup')}
        onSelectOnline={() => handleSelectGameMode('/online')}
        adCheckPending={!isWebPlatform && adCheckPending}
      />

      {/* Saved Game Modal */}
      <SavedGameModal
        open={savedGameModalOpen}
        onResume={handleResumeSavedGame}
        onNewGame={handleDeclineSavedGame}
        gameInfo={savedGameInfo}
      />

      {/* Tutorial Modal */}
      <TutorialModal
        open={tutorialModalOpen}
        onClose={handleTutorialClose}
        onCloseAndDontShowAgain={handleTutorialDontShowAgain}
      />

      {/* Changelog Modal */}
      <ChangelogModal
        open={changelogModalOpen}
        version={currentVersion}
        onClose={handleChangelogClose}
        onCloseAndDontShowAgain={handleChangelogCloseAndDontShow}
      />
    </>
  );
}

function App() {
  // Track page views automatically
  usePageTracking();

  useEffect(() => {
    // Initialize sound and vibration service
    soundVibrationService.initialize();
  }, []);

  return (
    <AuthProvider>
      <DeepLinkListener />
      <DeviceTokenRegistrar />
      <Routes>
        <Route path="/" element={
          <>
            <VersionCheckManager />
            <Home />
          </>
        } />
        <Route path="/setup" element={<GameSetup />} />
        <Route path="/instructions" element={<Instructions />} />
        <Route path="/ronda" element={<Ronda />} />
        <Route path="/baciga" element={<Baciga />} />
        <Route path="/online/login" element={<OnlineAuthScreen />} />
        <Route path="/oauth2/callback" element={<OAuthCallbackScreen />} />
        <Route path="/online" element={<RequireOnlineAuth><OnlineLobbyScreen /></RequireOnlineAuth>} />
        <Route path="/online/create" element={<RequireOnlineAuth><OnlineCreateRoomScreen /></RequireOnlineAuth>} />
        <Route path="/online/history" element={<RequireOnlineAuth><OnlineHistoryScreen /></RequireOnlineAuth>} />
        <Route path="/online/rankings" element={<RequireOnlineAuth><OnlineRankingsScreen /></RequireOnlineAuth>} />
        <Route path="/online/profile" element={<RequireOnlineAuth><OnlineProfileScreen /></RequireOnlineAuth>} />
        <Route path="/online/join/:code" element={<RequireOnlineAuth><JoinByLinkScreen /></RequireOnlineAuth>} />
        <Route path="/online/room/:roomId" element={<RequireOnlineAuth><WaitingRoomScreen /></RequireOnlineAuth>} />
        <Route path="/online/play/:roomId" element={<RequireOnlineAuth><OnlinePlayScreen /></RequireOnlineAuth>} />
        <Route path="/online/spectate/:roomId" element={<RequireOnlineAuth><OnlinePlayScreen spectator /></RequireOnlineAuth>} />
        <Route path="/admin" element={<RequireAdmin><AdminHomeScreen /></RequireAdmin>} />
        <Route path="/admin/usuarios" element={<RequireAdmin><AdminUsersScreen /></RequireAdmin>} />
        <Route path="/admin/partidas" element={<RequireAdmin><AdminRoomsScreen /></RequireAdmin>} />
        <Route path="/admin/sugerencias" element={<RequireAdmin><AdminSuggestionsScreen /></RequireAdmin>} />
        <Route path="/admin/errores" element={<RequireAdmin><AdminErrorsScreen /></RequireAdmin>} />
        <Route path="/admin/rankings" element={<RequireAdmin><AdminRankingsScreen /></RequireAdmin>} />
        <Route path="/admin/estadisticas" element={<RequireAdmin><AdminUsageStatsScreen /></RequireAdmin>} />
        <Route path="/admin/configuracion" element={<RequireAdmin><AdminAppVersionScreen /></RequireAdmin>} />
        <Route path="/admin/notificaciones" element={<RequireAdmin><AdminNotificationsScreen /></RequireAdmin>} />
      </Routes>
    </AuthProvider>
  );
}

export default App;
